import React from 'react';
import {
  Box,
  tooltipClasses,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import {
  COLOR_TEXT_GREY,
  COLOR_PRIMARY_2,
  COLOR_TEXT_4,
  COLOR_TEXT_2,
  COLOR_GREY_200,
} from 'src/utils/colors';
import { convertCurrency, formatCurrency, getGradeColor } from 'src/utils/functions';
import { useSelector } from 'react-redux';
import { exchangeRates } from 'src/utils/const';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';
import SubmarketMarketGrade from './SubmarketMarketGrade';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff0',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 320,
    fontSize: theme.typography.pxToRem(12),
    border: 'none',
  },
}));

const MetricBox = ({
  label,
  value,
  currency,
  isDisabled,
  handleOnSampleClick,
}: {
  label: string;
  value: string | number;
  currency: string | null;
  isDisabled: boolean;
  handleOnSampleClick: (plan: string) => void;
}) => (
  <Box sx={{ textAlign: 'left', position: 'relative' }}>
    <Typography variant="body1" sx={{ fontSize: '12px', color: COLOR_TEXT_4, mb: 0.5 }}>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontSize: '18px',
        fontWeight: 600,
        color: COLOR_TEXT_2,
        filter: isDisabled ? 'blur(4px)' : 'none',
      }}
    >
      {currency && exchangeRates[currency].symbol}
      {value}
    </Typography>
    {isDisabled && (
      <IconButton
        onClick={() => handleOnSampleClick('expert')}
        size="small"
        sx={{ transform: 'translateY(-10px)' }}
      >
        <LockIcon sx={{ color: COLOR_PRIMARY_2 }} />
      </IconButton>
    )}
  </Box>
);

const SubmarketTooltip = ({
  title,
  content,
  children,
  disable,
  placement,
  arrow,
  enterTouchDelay,
  leaveTouchDelay,
  minWidth,
  submarketData,
  handleOnSampleClick,
  isSubmarketAccessAllowed,
  setDashboardLayoutView,
  showCta,
  ctaSource,
  handleHideSubmarket,
  returnTooltipBodyOnly,
  hideMarketGrade,
  sx,
  marketGradeSx,
  blurSubmarketName,
}: {
  title: string | JSX.Element;
  content: JSX.Element | string;
  children: JSX.Element;
  disable?: boolean;
  placement?:
    | 'bottom'
    | 'left'
    | 'right'
    | 'top'
    | 'bottom-end'
    | 'bottom-start'
    | 'left-end'
    | 'left-start'
    | 'right-end'
    | 'right-start'
    | 'top-end'
    | 'top-start';
  arrow?: boolean;
  enterTouchDelay?: number;
  leaveTouchDelay?: number;
  minWidth?: number;
  submarketData: any;
  handleOnSampleClick: (plan: string) => void;
  isSubmarketAccessAllowed: boolean;
  setDashboardLayoutView: (view: string) => void;
  showCta?: boolean;
  ctaSource?: 'submarkets' | 'map';
  handleHideSubmarket?: (submarketId: string) => void;
  returnTooltipBodyOnly?: boolean;
  hideMarketGrade?: boolean;
  sx?: any;
  marketGradeSx?: any;
  blurSubmarketName?: boolean;
}) => {
  const currency = useSelector((state: any) => state.dashboard.currency);
  const convertAndFormatCurrency = (val: number) =>
    formatCurrency(String(convertCurrency(currency || 'USD', val)));

  const TooltipBody = (
    <Card
      sx={{
        minWidth: minWidth ? minWidth : 300,
        maxWidth: 320,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: '12px',
        overflow: 'hidden',
        p: 0,
        ...sx,
      }}
    >
      <CardContent sx={{ p: '16px !important' }}>
        {/* Header with title and rank */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#000',
              flex: 1,
              mr: 1,
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: 'row',
            }}
          >
            {!hideMarketGrade && (
              <SubmarketMarketGrade
                neighborhoodGrade={submarketData.neighborhood_grade}
                isSubmarketAccessAllowed={isSubmarketAccessAllowed}
                handleOnSampleClick={handleOnSampleClick}
                marketGradeSx={marketGradeSx}
                verifiedTier={submarketData.verified_tier}
              />
            )}
            <span style={{ filter: blurSubmarketName ? 'blur(5px)' : 'none' }}>
              {submarketData.submarket_name}
            </span>
          </Typography>

          {submarketData?.rank && (
            <Chip
              label={`#${submarketData.rank}`}
              size="small"
              sx={{
                backgroundColor: '#f5f5f5',
                color: '#666',
                fontSize: '12px',
                fontWeight: 500,
                height: '24px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          )}
        </Box>

        {/* 2x2 Metrics Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* First row: Supply and ADR */}
          <Grid item xs={6}>
            <MetricBox
              label="Supply"
              value={formatCurrency(String(submarketData.supply))}
              currency={null}
              isDisabled={false}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricBox
              label="Daily Rate"
              value={convertAndFormatCurrency(submarketData.adr)}
              currency={currency}
              isDisabled={!isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>

          {/* Second row: Occupancy and Revenue */}
          <Grid item xs={6}>
            <MetricBox
              label="Occupancy"
              value={`${submarketData.occupancy_rate}%`}
              currency={null}
              isDisabled={!isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
          <Grid item xs={6}>
            <MetricBox
              label="Revenue"
              value={convertAndFormatCurrency(submarketData.revenue)}
              currency={currency}
              isDisabled={!isSubmarketAccessAllowed}
              handleOnSampleClick={handleOnSampleClick}
            />
          </Grid>
        </Grid>

        {/* CTA Button */}
        {showCta && (
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={() =>
                isSubmarketAccessAllowed
                  ? ctaSource == 'submarkets'
                    ? setDashboardLayoutView('submarkets')
                    : window.open(submarketData.submarket_url, '_blank')
                  : handleOnSampleClick('expert')
              }
              sx={{
                backgroundColor: COLOR_PRIMARY_2,
                color: 'white',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                py: 1,
                '&:hover': {
                  backgroundColor: COLOR_PRIMARY_2 + 'AA',
                },
              }}
            >
              {ctaSource == 'submarkets' ? 'Compare Submarket' : 'View Submarket'}
              {ctaSource == 'map' && <OpenInNewIcon sx={{ ml: 1, fontSize: '16px' }} />}
            </Button>

            {handleHideSubmarket && (
              <Button
                fullWidth
                variant="text"
                color="primary"
                onClick={() => handleHideSubmarket(submarketData.submarket_id)}
                sx={{ mt: 1 }}
              >
                Hide Submarket
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  if (returnTooltipBodyOnly) {
    return TooltipBody;
  }

  return (
    <CustomTooltip
      PopperProps={{
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              rootBoundary: 'viewport',
            },
          },
        ],
      }}
      placement={placement}
      arrow={arrow}
      enterTouchDelay={enterTouchDelay ? enterTouchDelay : 0}
      leaveTouchDelay={leaveTouchDelay ? leaveTouchDelay : 5000}
      title={TooltipBody}
    >
      {children}
    </CustomTooltip>
  );
};

export default React.memo(SubmarketTooltip);
